'use client';

import * as React from 'react';
import { useState } from 'react';

import {
  ArrowLeft20Filled,
  ArrowRight20Filled,
  CalendarLtr20Filled,
} from '@fluentui/react-icons';
import {
  startOfWeek,
  addDays,
  format,
  subWeeks,
  addWeeks,
  parseISO,
} from 'date-fns';
import { tv } from 'tailwind-variants';
import type { VariantProps } from 'tailwind-variants';

import { cn } from '@avodah-inc/ui/utils';

import { Button } from '../../atoms/button';
import { CalendarDay } from '../../atoms/calendar-day';
import { Popover, PopoverContent, PopoverTrigger } from '../../atoms/popover';
import { UnstyledButton } from '../../atoms/unstyled-button';
import { Calendar } from '../calendar';

const calendarWeekVariants = tv({
  base: 'flex justify-center items-center gap-x-1 relative',
  variants: {
    variant: {
      default: 'w-fit',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type CalendarWeekProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof calendarWeekVariants> & {
    currentDate?: Date;
    daysToDisplay?: number;
    onDateSelect?: (selectedDate: Date) => void;
    selectedValue?: string;
    calenderTriggerTestId?: string;
    calenderInFormLeftArrowTestId?: string;
    calenderInFormRightArrowTestId?: string;
  };

export const CalendarWeek = React.forwardRef<HTMLDivElement, CalendarWeekProps>(
  (
    {
      currentDate = new Date(),
      daysToDisplay = 7,
      selectedValue: defaultValue,
      onDateSelect,
      className,
      variant,
      ...props
    },
    ref,
  ) => {
    const [date, setDate] = React.useState<Date>(() =>
      defaultValue ? parseISO(defaultValue) : currentDate,
    );

    const [isOpen, setIsOpen] = useState(false);
    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
      startOfWeek(defaultValue ? new Date(defaultValue) : currentDate),
    );

    const daysInWeekArray = Array.from({ length: daysToDisplay }, (_, index) =>
      addDays(currentWeekStart, index),
    );

    const handleDayClick = (index: number) => {
      const selectedDate = daysInWeekArray[index];

      if (!selectedDate) return;
      onDateSelect?.(selectedDate);
      setDate(selectedDate);
      setIsOpen(false);
    };

    const handleBackWeekClick = () => {
      setCurrentWeekStart(subWeeks(currentWeekStart, 1));
    };

    const handleForwardWeekClick = () => {
      setCurrentWeekStart(addWeeks(currentWeekStart, 1));
    };

    const handleSelect = React.useCallback((selectedDate: Date | undefined) => {
      if (!selectedDate) return;
      onDateSelect?.(selectedDate);
      setCurrentWeekStart(startOfWeek(selectedDate));
      setDate(selectedDate);
      setIsOpen(false);
    }, []);

    return (
      <div
        className={cn(
          calendarWeekVariants({
            variant,
            className,
          }),
        )}
        ref={ref}
        {...props}
      >
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <UnstyledButton
              className="bg-primary-50 h-10 w-10 items-center justify-center rounded active:translate-y-0.5"
              data-testid={props.calenderTriggerTestId}
            >
              <CalendarLtr20Filled className="text-primary-500" />
            </UnstyledButton>
          </PopoverTrigger>
          <PopoverContent className="z-50 w-auto">
            <Calendar
              initialFocus={false}
              mode="single"
              selected={date}
              onSelect={handleSelect}
              onTodayClick={() => {
                const selectedDate = new Date();
                handleSelect(selectedDate);
              }}
              leftArrowTestId={props.calenderInFormLeftArrowTestId}
              rightArrowTestId={props.calenderInFormRightArrowTestId}
            />
          </PopoverContent>
        </Popover>

        <div className="hidden lg:flex lg:items-center">
          <Button onPress={handleBackWeekClick} variant="text" isIconOnly>
            <ArrowLeft20Filled className="text-primary-500" />
          </Button>
          {daysInWeekArray.map((day, index) => {
            const isActive = day.toDateString() === date.toDateString();
            return (
              <CalendarDay
                key={index}
                size="lg"
                isCurrentDay={currentDate.toDateString() === day.toDateString()}
                isActive={isActive}
                onPress={() => {
                  handleDayClick(index);
                }}
              >
                {[format(day, 'd'), format(day, 'iii')]}
              </CalendarDay>
            );
          })}
          <Button onPress={handleForwardWeekClick} variant="text" isIconOnly>
            <ArrowRight20Filled className="text-primary-500" />
          </Button>
        </div>
      </div>
    );
  },
);

CalendarWeek.displayName = 'CalendarWeek';
